import { api } from './client';

/**
 * What this deployment can do, answered by the server rather than compiled in.
 *
 * ---------------------------------------------------------------------------
 * WHY THE SERVER DECIDES
 * ---------------------------------------------------------------------------
 * Whether a payment can be taken depends on credentials the app cannot see and
 * must never hold - they live in the backend's environment. The backend also
 * knows something an environment variable alone does not: whether a gateway is
 * actually *wired up* behind its payments port. Asking it is the only answer
 * that cannot disagree with what a booking request would really do.
 *
 * The alternative - a constant in the app - meant the two could drift, and the
 * drift is expensive in exactly one direction: an app offering to charge for a
 * booking the server will refuse with a 501.
 */
export type AppConfig = {
  /**
   * True when `paymentMode: 'online'` will be accepted.
   *
   * False means cash only: the customer settles with the professional after
   * the work is done, and nothing may claim a payment was taken.
   */
  paymentsEnabled: boolean;

  /**
   * Hours before the slot within which cancelling is still free.
   *
   * Ops-controlled - `booking.freeCancellationHours` - and quoted to the
   * customer in the cart, on the booking screen, and in the cancel sheet.
   */
  freeCancellationHours: number;

  /**
   * How close to its slot a booking can still be rescheduled - and how far
   * ahead the new slot must be. Ops-controlled (`booking.freeRescheduleHours`,
   * 2 by default); the server refuses inside it, this only words the policy.
   */
  rescheduleCutoffHours: number;

  /**
   * Percentage of the payable amount retained on a late cancellation.
   *
   * ---------------------------------------------------------------------------
   * A PERCENTAGE. THE APP USED TO PROMISE A FLAT ₹49.
   * ---------------------------------------------------------------------------
   * `CANCEL_FEE = 49` lived in `data/cancellation.ts` and was quoted in three
   * places. The server has never charged it: `cancellation-policy.ts` retains
   * `booking.lateCancellationFeePercent` of the payable amount, 25 by default.
   * A ₹2,000 booking cancelled late cost ₹500 against a promise of ₹49 - and
   * the comment beside that constant had already worried, in writing, about a
   * customer who "reads one and is charged by another".
   *
   * So the number is served rather than compiled, and it is a percentage
   * rather than rupees, because the rupee figure depends on the basket.
   */
  lateCancellationFeePercent: number;

  /**
   * Hours of notice a slot needs before it may be booked.
   *
   * The seam `data/booking.ts` predicted in writing: its `MIN_NOTICE_HOURS`
   * comment says the served version reads
   * `appConfig().minNoticeHours ?? MIN_NOTICE_HOURS`, and this is that setting
   * arriving. Dispatch has to find somebody, tell them and give them time to
   * travel, and how long that takes changes with the day.
   *
   * CLIENT-ONLY: the picker stops offering slots inside it, the API does not
   * refuse one. Widening it is a promise this app keeps, not one the server
   * enforces.
   */
  minNoticeHours: number;

  /**
   * How many days after today a slot may be booked: 3 means today and the
   * next three days. Ops-controlled - `booking.advanceBookingDays` - and
   * enforced: the API refuses a later slot on a customer booking.
   */
  advanceBookingDays: number;

  /**
   * How many photographs a review may carry.
   *
   * The server has enforced 3 since reviews existed; this app offered 5. Two
   * of every five photographs a customer attached were never going to arrive.
   */
  reviewMaxPhotos: number;

  /** How much a customer may write in a review. Client-only. */
  reviewMaxChars: number;

  /** Largest photo this app offers to upload, in megabytes. Client-only. */
  uploadMaxMb: number;

  /** How often an open chat thread re-reads. Client-only. */
  chatPollMs: number;

  /**
   * The support desk's number, or the empty string when there is none.
   *
   * Empty is the ordinary state and must be handled as "do not offer to call"
   * rather than dialled - the rider app's "Talk to us" prompt has been telling
   * people to ring a number it never printed.
   */
  supportPhone: string;

  /**
   * Who the invoice is from - set by ops under Finance in Settings. Each is the
   * empty string until they have filled it in, and the invoice leaves that line
   * OFF rather than printing a stand-in: a made-up GSTIN on a tax document is
   * worse than none.
   */
  invoiceSellerName: string;
  invoiceSellerGstin: string;
  invoiceSellerAddress: string;
  invoiceSacCode: string;

  /**
   * The number WhatsApp support messages go to, when it is not the one you
   * dial. Empty means "use `supportPhone`" - see `supportWhatsappNumber`,
   * which is the only thing that should read either of these two for chat.
   */
  supportWhatsapp: string;

  /** Default opening hours, `HH:mm`, before an address narrows them by city. */
  dayOpensAt: string;
  dayClosesAt: string;
  /** Minutes between one offered slot and the next. */
  slotStepMinutes: number;

  /**
   * Whether the picker DRAWS the starts a rule has closed, or just ends the
   * grid early.
   *
   * Off by default, and that is the whole point: a start the booking cannot
   * finish before closing can never be taken that day, so a column of
   * struck-through tiles tells the customer nothing they can act on. With it
   * off, a two-hour basket in a city shutting at 21:00 simply offers nothing
   * after 19:00.
   *
   * On, each one is drawn disabled with the time left under it and the
   * reason on tap - the state ops turns on to see the shape of a day, or to
   * explain one over the phone.
   *
   * It says nothing about FULLY BOOKED slots, which stay struck through
   * either way: those are real times somebody else has, a cancellation frees
   * one, and hiding them would make a busy day look like a short one.
   */
  showBlockedSlots: boolean;

  /**
   * True when Homingo+ is on sale - `membership.enabled`, ops-controlled.
   *
   * Off in the fallback, and that is the direction that matters: a config
   * that fails to load, or a server that predates the setting, hides the
   * membership rather than showing it. The server enforces the same setting
   * (no plans listed, new purchases refused), so this decides only what is
   * drawn, never what can be bought.
   */
  membershipEnabled: boolean;
};

/**
 * What the app believes before the server has answered.
 *
 * ---------------------------------------------------------------------------
 * THE SAFE DIRECTION TO BE WRONG IN, FIELD BY FIELD
 * ---------------------------------------------------------------------------
 * `paymentsEnabled: false` - cash only. If this request never lands the app
 * offers the flow that works without a gateway rather than one that ends in a
 * refused booking.
 *
 * The cancellation numbers match the server's own defaults in
 * `BookingCancellationService`, so an app running on the fallback quotes what
 * an unconfigured deployment would actually charge. They are the LAST resort,
 * not the answer: anything the server sends wins.
 */
const FALLBACK: AppConfig = {
  paymentsEnabled: false,
  freeCancellationHours: 6,
  rescheduleCutoffHours: 2,
  lateCancellationFeePercent: 25,
  minNoticeHours: 2,
  advanceBookingDays: 3,
  /*
   * THREE, which is what the server enforces - not the five this app used to
   * offer. Starting the fallback at the app's old number would have kept the
   * bug alive for anyone the config never reached.
   */
  reviewMaxPhotos: 3,
  reviewMaxChars: 500,
  uploadMaxMb: 5,
  chatPollMs: 4000,
  supportPhone: '',
  invoiceSellerName: '',
  invoiceSellerGstin: '',
  invoiceSellerAddress: '',
  invoiceSacCode: '',
  supportWhatsapp: '',
  dayOpensAt: '09:00',
  dayClosesAt: '21:00',
  slotStepMinutes: 30,
  showBlockedSlots: false,
  membershipEnabled: false,
};

let cached: AppConfig = FALLBACK;

/** Read at render time. Never destructure this at module scope - see `load`. */
export const appConfig = (): AppConfig => cached;

/** Shorthand for the one field anything outside this file cares about. */
export const onlinePaymentsEnabled = (): boolean => cached.paymentsEnabled;
export const membershipOnSale = (): boolean => cached.membershipEnabled;

/**
 * Fetch it once, at launch.
 *
 * Anonymous: this is read before anyone has signed in, and the endpoint is
 * public. A failure is swallowed rather than thrown - the app is perfectly
 * usable on the fallback, and a launch that dies because a capability probe
 * timed out would be a worse bug than the one this prevents.
 */
export const loadAppConfig = async (
  signal?: AbortSignal,
): Promise<AppConfig> => {
  try {
    /*
     * Merged over the fallback, not assigned in its place.
     *
     * The document grows on the server and old builds keep reading it, so the
     * reverse is true as well: a build newer than the deployment asks for
     * fields that are not there yet. Spreading means a missing field is the
     * compiled default rather than `undefined` leaking into a price or a
     * sentence - `lateCancellationFeePercent` reaching the cancel sheet as
     * undefined would render "undefined% retained".
     */
    /*
     * `?app=customer` so the answer carries only what this app can use.
     *
     * Not a security boundary - the endpoint is public and will serve any
     * audience to anyone who asks - it keeps the payload to the point and
     * stops a Pro-only field like the end-of-service gate turning up here for
     * somebody to wire into a customer screen by mistake.
     *
     * A deployment older than the parameter ignores it and answers with
     * everything, which spreads over the fallback exactly the same way.
     */
    const served = await api.get<Partial<AppConfig>>('/config?app=customer', {
      signal,
      anonymous: true,
    });
    cached = { ...FALLBACK, ...served };
  } catch {
    /*
     * The last good answer is kept rather than thrown away.
     *
     * This reset to `FALLBACK`, which was harmless while the call happened
     * exactly once at launch. It stops being harmless the moment anything
     * refetches: one failed refresh on a train would walk a correct policy
     * back to a compiled guess, and nothing on screen would say so.
     */
  }
  return cached;
};
